import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import SHA from 'sha.js';

import { completeLookYodaTransformer } from '../../application/transformers/complete-look-yoda.transformer';
import { frecuentlyBoughtProductsTransformer } from '../../application/transformers/frecuently-bought-together/frecuently-bought-together.transformer';
import { otherYouMayLikeTransformer } from '../../application/transformers/other-you-may-like.transformer';
import { recommendedForYouProductsTransformer } from '../../application/transformers/recommended-for-you/recommended-for-you-api.transformer';
import { getTopSalesProductsIds, topSalesProductsTransformer } from '../../application/transformers/top-sales.transformer';
import { ApiFrecuentlyBoughtTogetherResponse } from '../../domain/models/api-frecuently-bought-together.model';
import { ApiOtherYouMayLikeResponse } from '../../domain/models/api-other-you-may-like.model';
import { ApiRecommendedForYouResponse } from '../../domain/models/api-recommended-for-you.model';
import { ApiTopSalesTrendsResponse, ApiTopSalesRecommendationsResponse } from '../../domain/models/api-top-sales.model';
import { ApiCompleteLookYodaResponse, SectionNameYoda } from '../../domain/models/complete-look-yoda.model';
import {
    ApiRecommendationsByPlacementErrorResponse,
    ApiRecommendationsByPlacementSuccessResponse,
    RecommendationsByPlacementParams,
} from '../../domain/models/recommendations-by-placement.model';
import {
    RelatedProductsRepositoryInterface,
} from '../../domain/related-products-repository.interface';
import { ApiRelatedProductsService } from '../api-relateds-products/api-relateds-products.service';

import {
    recommendationsByPlacementTransformer,
} from './transformers/recommendations-by-placement.transformer';

import { ProductUid } from '@shared/product/related-products/domain/models/related-products.models';

@Injectable()
export class RelatedProductsRepository implements RelatedProductsRepositoryInterface {
    constructor(
        private readonly apiRelatedProductsService: ApiRelatedProductsService,
    ) {}

    public getRelatedRecommendedForYou$(
        storeId: string,
        hashedMail: string,
        partNumber: string,
        section: number
    ): Observable<ProductUid[]> {
        return this.apiRelatedProductsService.getRecommendedForYouProducts$(
            storeId,
            hashedMail,
            partNumber,
            section
        ).pipe(
            map((response: ApiRecommendedForYouResponse): ProductUid[] => {
                return recommendedForYouProductsTransformer(response.data);
            })
        );
    }

    public getTopSalesRelatedProducts$(storeId: string, section: number): Observable<ProductUid[]> {
        return this.apiRelatedProductsService.getTopSalesTrendsProducts$(storeId, section).pipe(
            map((response: ApiTopSalesTrendsResponse): ProductUid[] => topSalesProductsTransformer(response.data))
        );
    }

    public getTopSalesProductsByUserAndSection$(
        storeId: string,
        section: number,
        campaign: string,
        userEmail?: string
    ): Observable<number[]> {
        const hashedMail = userEmail ? SHA('sha256').update(userEmail).digest('hex') : undefined;

        return this.apiRelatedProductsService.getTopSalesRecommendationProducts$(
            storeId,
            section,
            campaign,
            hashedMail
        ).pipe(
            map((response: ApiTopSalesRecommendationsResponse): number[] => getTopSalesProductsIds(response.data))
        );
    }

    public getRelatedsFBT$(productIds: string[], storeId: string): Observable<ProductUid[]> {
        return this.apiRelatedProductsService.getFrencuentlyBoughtProducts$(productIds, storeId).pipe(
            map((response: ApiFrecuentlyBoughtTogetherResponse): ProductUid[] => {
                return frecuentlyBoughtProductsTransformer(response.data);
            })
        );
    }

    public getCompleteLookYoda$(
        campaign: string,
        productId: string,
        section: SectionNameYoda,
        storeId: string,
    ): Observable<ProductUid[]> {
        return this.apiRelatedProductsService.getCompleteLookYoda$(
            campaign,
            section,
            productId,
            storeId,
        ).pipe(
            map((response: ApiCompleteLookYodaResponse) => {
                return completeLookYodaTransformer(response);
            })
        );
    }

    public getRecommendationsByPlacement$(params: RecommendationsByPlacementParams): Observable<ProductUid[]> {
        return this.apiRelatedProductsService.getRecommendationsByPlacement$(params).pipe(
            map((response: ApiRecommendationsByPlacementSuccessResponse) =>
                recommendationsByPlacementTransformer(response, params.type)),
            catchError((_error: ApiRecommendationsByPlacementErrorResponse) => of([])),
        );
    }

    public getOtherYouMayLike$(
        campaign: string,
        productId: string,
        section: string,
        sessionId: string,
        storeId: string,
    ): Observable<ProductUid[]> {
        return this.apiRelatedProductsService.getOtherYouMayLike$(
            campaign,
            productId,
            section,
            sessionId,
            storeId
        ).pipe(
            map((response: ApiOtherYouMayLikeResponse) => {
                return otherYouMayLikeTransformer(response);
            })
        );
    }
}
